import RestService from "./rest.service";
import type { FetchPageServiceParameters } from "~/core/composables/useFetchPageV2";
import type { OperationModel } from "~/core/models/OperationModel.interface";
import type { PaginatedList } from "~/core/models/PaginatedList.interface";
import type { CheckOperationParameters } from "~/modules/dtwin/models/CheckOperationParameters.interface";
import type { DtwinCheckRunningOperation } from "~/modules/dtwin/models/DtwinCheckRunningOperation.type";
import type { DtwinCreateOperation } from "~/modules/dtwin/models/DtwinCreateOperation.interface";
import type { DtwinOperation } from "~/modules/dtwin/models/DtwinOperation.interface";
import type { DtwinUpdateOperation } from "~/modules/dtwin/models/DtwinUpdateOperation.interface";
import organizationService from "~/modules/organization/services/organization.service";
import type { FiltersSelection } from "~/types";

/**
 * Operation manager for the Dtwins
 */
export class OperationManagerService extends RestService {
  /**
   * Get the paginated operation list filtered and sorted
   * @param page
   * @param pageSize
   * @param filter
   * @param sort
   * @returns
   */
  getOperations({
    organizationCode,
    page,
    pageSize,
    searchParams,
    sort,
  }: FetchPageServiceParameters): Promise<PaginatedList<DtwinOperation>> {
    const params: Record<string, string | number | FiltersSelection> = {};

    if (page) {
      params.page = page;
    }
    if (pageSize) {
      params.pageSize = pageSize;
    }
    if (searchParams) {
      params.filter = searchParams;
    }
    if (sort) {
      params.sort = sort;
    }

    return this.$apiV2.get(
      `${this.getOrganizationOperationsPath(organizationCode)}`,
      { params },
    );
  }

  getOperation(
    organizationCode: number,
    uuid: string,
  ): Promise<DtwinOperation> {
    return this.$apiV2.get(
      `${this.getOrganizationOperationsPath(organizationCode)}/${uuid}`,
    );
  }

  /**
   * Check if there is operations in the devices list having the status:
   * - SCHEDULED
   * - QUEUED
   * - PENDING
   * - DELIVERED
   * - RUNNING
   * - RETRY
   * - CANCELLING
   * @param type possible values 'FIRM_UPDATE', 'OPTIONS'
   * @param deviceTwinUid
   * @param targetVersion
   * @param creationMode
   * @param filter RSQL filter, example launchPlannedAt>=2023-12-15T10:56:32.476Z;createdAt<2023-12-15T10:56:32.476Z
   * @param organizationCode
   */
  checkOperation({
    type,
    deviceTwinUid,
    targetVersion,
    creationMode,
    filter,
    organizationCode,
  }: CheckOperationParameters): Promise<DtwinCheckRunningOperation> {
    const params: Record<string, string | string[]> = {
      type,
      deviceTwinUid,
    };

    if (targetVersion) {
      params.targetVersion = targetVersion;
    }

    if (creationMode) {
      params.creationMode = creationMode;
    }

    if (filter) {
      params.filter = filter;
    }

    return this.$apiV2.get(
      `${this.getOrganizationOperationsPath(organizationCode)}/check`,
      { params },
    );
  }

  /**
   * Get the paginated operation model list filtered and sorted
   * @param organizationCode
   * @param page
   * @param pageSize
   * @param searchParams
   * @param sort
   * @param inputParams
   * @returns
   */
  getModels({
    organizationCode,
    page,
    pageSize,
    searchParams,
    sort,
    params: inputParams,
  }: FetchPageServiceParameters): Promise<PaginatedList<OperationModel>> {
    const params: { [p: string]: unknown } = inputParams
      ? Object.fromEntries(inputParams)
      : {};

    if (page) {
      params.page = page;
    }
    if (pageSize) {
      params.pageSize = pageSize;
    }
    if (searchParams) {
      params.filter = searchParams;
    }
    if (sort) {
      params.sort = sort;
    }
    return this.$apiV2.get(
      `${this.getOrganizationOperationsPath(organizationCode)}/models`,
      { params },
    );
  }

  /**
   * Create a new device twin operation
   * @param operation
   * @param organizationCode
   * @returns
   */
  createOperation(
    organizationCode: number,
    operation: DtwinCreateOperation,
  ): Promise<DtwinOperation> {
    return this.$apiV2.post(
      `${this.getOrganizationOperationsPath(organizationCode)}`,
      operation,
    );
  }

  /**
   * Update device twin operation values
   * @param uuid
   * @param operation
   * @param organizationCode
   * @returns
   */
  updateOperation(
    organizationCode: number,
    uuid: string,
    operation: DtwinUpdateOperation,
  ): Promise<void> {
    return this.$apiV2.patch(
      `${this.getOrganizationOperationsPath(organizationCode)}/${uuid}`,
      operation,
    );
  }

  /**
   * @param uuid
   * @param organizationCode
   * @returns
   */
  async cancel(organizationCode: number, uuid: string): Promise<void> {
    return this.$apiV2.post(
      `${this.getOrganizationOperationsPath(organizationCode)}/${uuid}/cancel`,
    );
  }

  private getOrganizationOperationsPath(organizationCode: number | undefined) {
    return `${organizationService.getOrganizationPath(organizationCode)}/operations`;
  }
}

export default new OperationManagerService();
